import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { getSectorLogo } from '../utils/sectorUtils';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSectorsOpen, setIsSectorsOpen] = useState(false);
  const [sectors, setSectors] = useState([]);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const location = useLocation();

  // Déterminer le logo à afficher en fonction de la page actuelle
  const currentLogo = useMemo(() => {
    const pathname = location.pathname;

    // Vérifier si on est sur une page de secteur
    if (pathname.startsWith('/secteurs/')) {
      const sectorSlug = pathname.split('/secteurs/')[1];
      const sectorLogo = getSectorLogo(sectorSlug);
      if (sectorLogo) return sectorLogo;
    }

    // Logo par défaut
    return '/images/logo/nellfaa-logo.png';
  }, [location.pathname]);

  // Charger les secteurs depuis l'API
  useEffect(() => {
    const fetchSectors = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/sectors/`);
        setSectors(response.data);
      } catch (error) {
        console.error('Erreur lors du chargement des secteurs:', error);
      }
    };

    fetchSectors();
  }, []);

  // Fermer le menu déroulant quand on clique en dehors
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsSectorsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleSectors = () => {
    setIsSectorsOpen(!isSectorsOpen);
  };

  // Gestion clavier pour le bouton et le menu des secteurs
  const handleSectorsButtonKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsSectorsOpen((prev) => !prev);
      // Focus sur le premier élément du menu après ouverture
      setTimeout(() => {
        const items = menuRef.current?.querySelectorAll('.dropdown-item');
        if (items && items.length > 0) {
          items[0].focus();
        }
      }, 0);
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsSectorsOpen(true);
      setTimeout(() => {
        const items = menuRef.current?.querySelectorAll('.dropdown-item');
        if (items && items.length > 0) {
          items[0].focus();
        }
      }, 0);
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIsSectorsOpen(true);
      setTimeout(() => {
        const items = menuRef.current?.querySelectorAll('.dropdown-item');
        if (items && items.length > 0) {
          items[items.length - 1].focus();
        }
      }, 0);
    }
  };

  const handleMenuKeyDown = (e) => {
    const items = Array.from(menuRef.current?.querySelectorAll('.dropdown-item') || []);
    const currentIndex = items.findIndex((el) => el === document.activeElement);

    if (e.key === 'Escape') {
      e.preventDefault();
      setIsSectorsOpen(false);
      // Revenir le focus sur le bouton
      dropdownRef.current?.querySelector('button.nav-link')?.focus();
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIndex = (currentIndex + 1) % items.length;
      items[nextIndex]?.focus();
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = (currentIndex - 1 + items.length) % items.length;
      items[prevIndex]?.focus();
    }
    if (e.key === 'Home') {
      e.preventDefault();
      items[0]?.focus();
    }
    if (e.key === 'End') {
      e.preventDefault();
      items[items.length - 1]?.focus();
    }
  };

  return (
    <header className="header">
      <div className="container">
        <div className="header-content">
          <Link to="/" className="logo">
            <img src={currentLogo} alt="Nell'Faa Groupe Logo" className="logo-image" />

          </Link>

          <nav className={`nav ${isMenuOpen ? 'nav-open' : ''}`}>
            <Link
              to="/"
              className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}
              onClick={() => {
                setIsMenuOpen(false);
                setIsSectorsOpen(false);
              }}
            >
              Accueil
            </Link>

            <div
              className={`dropdown ${isSectorsOpen ? 'show' : ''}`}
              ref={dropdownRef}
            >
              <button
                type="button"
                className="nav-link"
                onClick={toggleSectors}
                onKeyDown={handleSectorsButtonKeyDown}
                aria-expanded={isSectorsOpen}
                aria-haspopup="true"
                aria-controls="sectors-menu"
              >
                Nos Secteurs
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                  <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <div
                id="sectors-menu"
                className="dropdown-menu"
                role="menu"
                ref={menuRef}
                onKeyDown={handleMenuKeyDown}
              >
                {sectors.map((sector) => (
                  <Link
                    key={sector.id}
                    to={`/secteurs/${sector.name}`}
                    className="dropdown-item"
                    role="menuitem"
                    onClick={() => {
                      setIsMenuOpen(false);
                      setIsSectorsOpen(false);
                    }}
                  >
                    {sector.display_name}
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to="/actualites"
              className={`nav-link ${location.pathname === '/actualites' || location.pathname.startsWith('/actualites/') ? 'active' : ''}`}
              onClick={() => {
                setIsMenuOpen(false);
                setIsSectorsOpen(false);
              }}
            >
              Actualités
            </Link>

            <Link
              to="/a-propos"
              className={`nav-link ${location.pathname === '/a-propos' ? 'active' : ''}`}
              onClick={() => {
                setIsMenuOpen(false);
                setIsSectorsOpen(false);
              }}
            >
              À Propos
            </Link>

            <Link
              to="/contact"
              className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`}
              onClick={() => {
                setIsMenuOpen(false);
                setIsSectorsOpen(false);
              }}
            >
              Contact
            </Link>
          </nav>

          <button
            className={`menu-toggle ${isMenuOpen ? 'active' : ''}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Menu"
            aria-expanded={isMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
